<template>
  <v-row v-if="searchAction">
    <v-col cols="12">
      <slot name="filter" v-bind="{}" />
    </v-col>
  </v-row>

  <v-data-table
    :hover="!disableHover"
    :headers="headersRef"
    :items="computedItems || []"
    :items-per-page="limit"
    :loading="loading || actionLoading"
    :loading-text="`${$t('common.action.loading')}...`"
    :single-expand="true"
    class="elevation-1"
    hide-default-footer
    :show-expand="showExpand"
    :v-model:expanded="expanded"
  >
    <template v-slot:item="{ item }">
      <slot name="item" v-bind="{ item: item }" />
    </template>

    <template v-slot:bottom="{}">
      <v-divider />
      <v-layout class="pr-2 py-1">
        <span />
        <v-spacer />
        <v-btn
          icon="mdi-chevron-left"
          class="mr-3"
          :disabled="busy || pageIndex === 0"
          @click="previous"
          variant="plain"
        />
        <v-btn
          icon="mdi-chevron-right"
          :disabled="busy || !entityStore.hasMore"
          @click="next"
          variant="plain"
        />
      </v-layout>
    </template>
  </v-data-table>
</template>
<script lang="ts" setup generic="T extends { id?: string } = { id?: string }">
import { computed, onMounted, ref, watch } from "vue";
import { getActivePinia, type Pinia } from "pinia";
import type { DataTableHeader } from "vuetify";

// Pinia keeps the registry of active stores on the internal `_s` map,
// excluded from the public release types.
type PiniaWithRegistry = Pinia & { _s: Map<string, unknown> };

// Stores consumed by this table all expose cursor-based Stripe pagination
// state plus fetch/search actions that accept api options.
interface TableApiOptions {
  params: {
    limit: number;
    page: string | undefined;
    starting_after: string | undefined;
    ending_before: string | undefined;
  };
  headers: Record<string, string>;
}

interface PaginatedTableStore {
  [key: string]: unknown;
  nextPage?: string;
  startingAfter?: string;
  endingBefore?: string;
  hasMore: boolean;
  searchCurrentPage?: string;
}

const props = withDefaults(
  defineProps<{
    headers?: DataTableHeader<T>[];
    fetchAction?: string;
    searchAction?: string;
    itemsGetter?: string;
    storeNamespace?: string;
    limit?: number;
    paginationScope?: string;
    showExpand?: boolean;
    actionLoading?: boolean;
    requestFetchAgain?: boolean;
    disableHover?: boolean;
    skipInitialFetchWhenItemsExist?: boolean;
  }>(),
  {
    headers: () => [],
    fetchAction: "",
    searchAction: "",
    itemsGetter: "",
    storeNamespace: "",
    limit: 10,
    paginationScope: "",
    showExpand: false,
    actionLoading: false,
    requestFetchAgain: false,
    disableHover: false,
    skipInitialFetchWhenItemsExist: false,
  },
);

const storeManager = getActivePinia() as PiniaWithRegistry | undefined;

const expanded = ref([]);
const loading = ref(false);
const busy = computed(() => loading.value || props.actionLoading);
const pageIndex = ref(0);
const headersRef = ref(props.headers);
const entityStore = storeManager?._s.get(props.storeNamespace) as PaginatedTableStore;
const computedItems = computed(() => entityStore[props.itemsGetter] as T[]);
const searchHistory = ref<(string | undefined)[]>([undefined]);

onMounted(() => {
  const existingItems = entityStore[props.itemsGetter];
  if (
    props.skipInitialFetchWhenItemsExist &&
    Array.isArray(existingItems) &&
    existingItems.length > 0
  ) {
    return;
  }

  fetchData();
});

watch(
  () => props.requestFetchAgain,
  (requested) => {
    if (requested) fetchData();
  },
);

const resetPagination = () => {
  pageIndex.value = 0;
  searchHistory.value = [undefined];
  entityStore.searchCurrentPage = undefined;
};

watch(() => props.paginationScope, resetPagination, { flush: "sync" });
watch(
  computedItems,
  () => {
    // Reapplying unchanged filters and other external refreshes load page one
    // without changing paginationScope.
    if (!loading.value) resetPagination();
  },
  { flush: "sync" },
);

const fetchData = async (type?: "next" | "prev"): Promise<void> => {
  if (busy.value || (type === "prev" && pageIndex.value === 0)) return;
  if (type === "next" && !entityStore.hasMore) return;

  const targetIndex =
    type === "next" ? pageIndex.value + 1 : type === "prev" ? pageIndex.value - 1 : 0;
  const currentPage = type === "next" ? entityStore.nextPage : searchHistory.value[targetIndex];
  // Filter controls also fetch directly, so derive list cursors from the visible
  // rows rather than markers left over from the table's last request.
  const rows = computedItems.value;
  const apiOptions: TableApiOptions = {
    params: {
      limit: props.limit,
      page: type ? currentPage : undefined,
      starting_after: type === "next" ? rows.at(-1)?.id : undefined,
      ending_before: type === "prev" ? rows[0]?.id : undefined,
    },
    headers: {},
  };
  const scope = props.paginationScope;
  const action = props.searchAction || props.fetchAction;
  const actionFn = entityStore[action] as (options: TableApiOptions) => Promise<boolean>;
  loading.value = true;
  try {
    const succeeded = await actionFn(apiOptions);
    if (!succeeded || scope !== props.paginationScope) {
      if (computedItems.value !== rows) resetPagination();
      return;
    }

    if (!type) searchHistory.value = [undefined];
    searchHistory.value[targetIndex] = apiOptions.params.page;
    pageIndex.value = targetIndex;
    entityStore.searchCurrentPage = apiOptions.params.page;
    const data = computedItems.value;
    entityStore.startingAfter = data.at(-1)?.id;
    entityStore.endingBefore = data[0]?.id;
  } finally {
    loading.value = false;
  }
};

const previous = () => {
  fetchData("prev");
};
const next = () => {
  fetchData("next");
};

defineExpose({
  fetchData,
});

defineSlots<{
  item: (props: { item: T }) => unknown;
  filter: () => unknown;
}>();
</script>
